import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { CheckCircle, XCircle, Clock } from "lucide-react";
import { searchLogsApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface SearchLogViewProps {
  tripId: string;
}

interface SearchLog {
  id: string;
  route_id: string;
  status: string;
  error_message: string | null;
  flights_found: number;
  duration_ms: number;
  created_at: string;
  origin: string;
  destination: string;
}

export default function SearchLogView({ tripId }: SearchLogViewProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["search-logs", tripId],
    queryFn: () => searchLogsApi.getByTrip(tripId),
  });

  const logs: SearchLog[] = data?.logs || [];

  if (isLoading) return null;
  if (logs.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Search Log</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {logs.map((log: SearchLog) => (
            <div key={log.id} className="flex items-center gap-3 text-sm py-1.5 border-b border-border last:border-0">
              {log.status === "success" ? (
                <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600 shrink-0" />
              )}
              <span className="text-muted-foreground min-w-0 truncate">
                {log.origin} &rarr; {log.destination}
              </span>
              <Badge variant="secondary" className="text-xs shrink-0">
                {log.flights_found} flights
              </Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0 ml-auto">
                <Clock className="h-3 w-3" />
                {log.duration_ms}ms
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {(() => {
                  try { return format(parseISO(log.created_at), "MMM dd h:mm a"); }
                  catch { return log.created_at; }
                })()}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
